import styled, {css} from 'styled-components'

export const StyledHistoryRoot = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing * 3}px;
        min-height: 240px;
    `
)

export const StyledHistoryState = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textSm};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${spacing * 3}px;
        padding: ${spacing * 10}px ${spacing * 4}px;
        color: ${palette.neutral[500]};
        text-align: center;
    `
)

export const StyledHistoryError = styled.div(
    ({theme: {palette, spacing, typography}}) => css`
        ${typography.textSm};
        padding: ${spacing * 3}px;
        background: ${palette.danger[50]};
        border: 1px solid ${palette.danger[200]};
        border-radius: 8px;
        color: ${palette.danger[700]};
    `
)

export const StyledHistoryList = styled.ul(
    ({theme: {spacing}}) => css`
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: ${spacing * 2.5}px;
    `
)

export const StyledIssueCard = styled.li(
    ({theme: {palette}}) => css`
        border: 1px solid ${palette.neutral[200]};
        border-radius: 8px;
        background: ${palette.neutral.white};
        overflow: hidden;
    `
)

export const StyledIssueHeader = styled.div(
    ({theme: {palette, spacing, shadows}}) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${spacing * 3}px;
        width: 100%;
        padding: ${spacing * 3}px;
        cursor: pointer;
        text-align: left;

        &:hover {
            background: ${palette.neutral[50]};
        }

        &:focus-visible {
            outline: none;
            box-shadow:
                ${shadows.xs},
                0px 0px 0px 4px ${palette.neutral['100']};
        }
    `
)

export const StyledCommentsTrigger = styled.span(
    ({theme: {spacing}}) => css`
        display: inline-flex;
        align-items: center;
        gap: ${spacing}px;
    `
)

export const StyledIssueHeaderMain = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing}px;
        min-width: 0;
    `
)

export const StyledIssueTitle = styled.span(
    ({theme: {palette, typography}}) => css`
        ${typography.textSm};
        font-weight: 600;
        color: ${palette.neutral[900]};
    `
)

export const StyledIssueMeta = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textXs};
        display: flex;
        align-items: center;
        gap: ${spacing * 2}px;
        color: ${palette.neutral[500]};
    `
)

export const StyledIssueHeaderRight = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        align-items: center;
        gap: ${spacing * 2}px;
        flex-shrink: 0;
    `
)

export const StyledStatus = styled.span(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textXs};
        display: inline-flex;
        align-items: center;
        gap: ${spacing}px;
        font-weight: 500;
        color: ${palette.neutral[700]};
        white-space: nowrap;
    `
)

export const StyledCompletedAt = styled.span(
    ({theme: {palette}}) => css`
        color: ${palette.neutral[500]};
        font-weight: 400;
    `
)

export const StyledStatusDot = styled.span<{$color: string}>(
    ({$color}) => css`
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${$color};
        flex-shrink: 0;
    `
)

export const StyledChevron = styled.span<{$open: boolean}>(
    ({$open, theme: {palette}}) => css`
        display: inline-flex;
        color: ${palette.neutral[500]};
        transition: transform 0.15s ease;
        transform: rotate(${$open ? 180 : 0}deg);
    `
)

export const StyledIssueBody = styled.div(
    ({theme: {spacing, palette}}) => css`
        padding: ${spacing * 3}px;
        border-top: 1px solid ${palette.neutral[200]};
        max-height: 320px;
        overflow-y: auto;
    `
)

export const StyledMarkdown = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textSm};
        color: ${palette.neutral[700]};
        word-break: break-word;

        & > *:first-child {
            margin-top: 0;
        }

        & > *:last-child {
            margin-bottom: 0;
        }

        p {
            margin: ${spacing * 2}px 0;
            line-height: 1.5;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            ${typography.textSm};
            font-weight: 600;
            color: ${palette.neutral[900]};
            margin: ${spacing * 3}px 0 ${spacing * 1.5}px;
        }

        ul,
        ol {
            margin: ${spacing * 2}px 0;
            padding-left: ${spacing * 5}px;
            display: flex;
            flex-direction: column;
            gap: ${spacing * 0.5}px;
        }

        a {
            color: ${palette.primary[600]};
            text-decoration: underline;
        }

        code {
            ${typography.textXs};
            font-family: monospace;
            background: ${palette.neutral[100]};
            padding: 1px 4px;
            border-radius: 4px;
        }

        img {
            max-width: 100%;
            height: auto;
            border-radius: 6px;
            margin: ${spacing}px 0;
        }

        hr {
            border: none;
            border-top: 1px solid ${palette.neutral[200]};
            margin: ${spacing * 3}px 0;
        }

        table {
            border-collapse: collapse;
            width: 100%;
            margin: ${spacing * 2}px 0;
        }

        th,
        td {
            ${typography.textXs};
            border: 1px solid ${palette.neutral[200]};
            padding: ${spacing * 1.5}px ${spacing * 2}px;
            text-align: left;
            vertical-align: top;
        }

        th {
            background: ${palette.neutral[50]};
            font-weight: 600;
            color: ${palette.neutral[800]};
        }
    `
)

export const StyledEmptyBody = styled.div(
    ({theme: {palette, typography}}) => css`
        ${typography.textSm};
        color: ${palette.neutral[400]};
        font-style: italic;
    `
)
